/* eslint-disable camelcase */

// node modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import withStyles from 'isomorphic-style-loader/withStyles';
import NoSsr from '@material-ui/core/NoSsr';
import classNames from 'classnames';

// app modules: components
// eslint-disable-next-line import/no-named-as-default
import Advert from 'components/Advert';
import AppearsIn from 'components/AppearsIn';
import Content from 'components/Content';
import ContentExtra from 'components/ContentExtra';
import ContentRaw from 'components/ContentRaw';
import GeneralActions from 'components/GeneralActions';
import GeneralHeader from 'components/GeneralHeader';
import HeadContent from 'components/HeadContent';
import InlinePaywall from 'components/InlinePaywall';
import withUserAccessLogger from 'components/UserAccessLogger';
import RecipeImage from 'components/RecipeImage';

// app modules: props
import NestedContentProps from 'components/NestedContent/NestedContentProps';
import RecipeImageProps from 'components/RecipeImage/RecipeImageProps';
import VideosProps from 'components/Videos/VideosProps';

// app modules: services
import { sendEvent } from 'services/analytics';
import assets from 'services/assets';
import route from 'services/route';
import { getImageSize } from 'services/image';
import { getSharingText } from 'services/socialShare';
import bookProgress from 'services/bookProgress';

// assets
import CssRecipe from 'components/Recipe/Recipe.scss';
import { CONTENT_TYPES } from '../../constants';

export class SectionComponent extends Component {
  static propTypes = {
    alacarte: PropTypes.bool,
    baseUrl: PropTypes.string,
    userId: PropTypes.number,
    canCopy: PropTypes.bool,
    canPrint: PropTypes.bool,
    checkingAccess: PropTypes.bool,
    enableSidebarAd: PropTypes.bool,
    forceSsr: PropTypes.bool,
    loading: PropTypes.bool,
    paywallError: PropTypes.string,
    previewOnly: PropTypes.bool,
    refreshAccessPolicy: PropTypes.func,
    section: PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.arrayOf(PropTypes.string),
      title_raw: PropTypes.arrayOf(PropTypes.string),
      subtitle: PropTypes.arrayOf(PropTypes.string),
      subtitle_lang: PropTypes.arrayOf(PropTypes.string),
      subtitle_raw: PropTypes.arrayOf(PropTypes.string),
      is_preview: PropTypes.bool,
      is_skeleton: PropTypes.bool,
      hash: PropTypes.string,
      book: PropTypes.shape({
        signature_: PropTypes.string,
        isbn: PropTypes.string,
        title: PropTypes.string,
        image_name: PropTypes.string,
        image_permission: PropTypes.bool,
        image_credit: PropTypes.string,
        publication_date: PropTypes.string,
        ranking_recommendations: PropTypes.number,
        recipe_copyright_line: PropTypes.string,
        licensor: PropTypes.string,
        authors: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            first_name: PropTypes.string,
            last_name: PropTypes.string,
            avatar_name: PropTypes.string,
            is_author: PropTypes.bool,
          }),
        ),
      }),
      graphics: RecipeImageProps.graphics,
      nested_content: NestedContentProps.content,
      parents: NestedContentProps.content,
      videos: VideosProps.videos,
      chapter: PropTypes.number,
      content_raw: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    usingAdminAccess: PropTypes.bool,
  };

  static defaultProps = {
    alacarte: false,
    baseUrl: '',
    userId: '',
    canCopy: true,
    canPrint: true,
    checkingAccess: false,
    enableSidebarAd: false,
    forceSsr: false,
    loading: false,
    paywallError: null,
    previewOnly: false,
    refreshAccessPolicy: () => {},
    usingAdminAccess: false,
  };

  componentDidMount() {
    const { baseUrl, previewOnly } = this.props;
    const { id, title, book, chapter } = this.props.section;

    if (!previewOnly) {
      if (window.intercom) {
        const url = `${baseUrl}${route.content(id, title)}`;
        window.intercom.logEvent('section-view', {
          url,
        });
      }
      sendEvent({
        event: 'Content.Section.View',
        id,
        book: book.title,
        chapter,
      });
    }
  }

  componentDidUpdate() {
    const { userId } = this.props;
    const { id, book } = this.props.section;

    bookProgress.saveBookProgress(book.isbn, userId, id);
  }

  render() {
    const {
      alacarte,
      canPrint,
      canCopy,
      checkingAccess,
      enableSidebarAd,
      forceSsr,
      loading,
      paywallError,
      previewOnly,
      refreshAccessPolicy,
      section,
      usingAdminAccess,
    } = this.props;
    const {
      book,
      content_raw,
      graphics,
      hash,
      id,
      instagram_author_name,
      instagram_html,
      instagram_post_url,
      instagram_thumbnail_height,
      instagram_thumbnail_url,
      instagram_thumbnail_width,
      instagram_width,
      is_preview,
      is_skeleton,
      nested_content,
      next,
      parents,
      prev,
      primary_image,
      subtitle_raw,
      subtitle_lang,
      subtitle,
      title_raw,
      title_with_prefix,
      title,
      videos,
    } = section;

    const toRenderTitle = title_with_prefix ? title_with_prefix.join(' ') : null;
    const toRenderSubtitle = subtitle ? subtitle.join(' ') : null;
    const { image_name } = section.book;
    const bookCoverUrl = assets.coverUrl(image_name, {
      resize: 'fit-in',
      width: Content.COVER_IMAGE_SIZE,
      height: Content.COVER_IMAGE_SIZE,
      webp: false,
    });
    const sharingImage =
      getImageSize(primary_image, 'share', size => size.w >= 1000 || size.h >= 1000) ||
      getImageSize(
        primary_image,
        'share',
        size => size.w >= 100 && size.w <= 2000 && size.h <= 2000,
        { searchHighToLow: true },
      );

    const sectionData = {
      '@context': 'http://schema.org/',
      '@type': 'CreativeWork',
      name: toRenderTitle,
      image: sharingImage?.src || bookCoverUrl,
      isAccessibleForFree: 'False',
      hasPart: {
        '@type': 'WebPageElement',
        isAccessibleForFree: 'False',
        cssSelector: '.paywalled-content',
      },
    };

    const textToShare = getSharingText(toRenderTitle, book, book.authors);

    const abovePaywall = (
      <React.Fragment>
        <Advert
          className="margin-bottom-10 margin-top-10"
          slotName="leaderboard-header"
          format="leaderboard"
          fullWidthResponsive={false}
          ezoicPlaceholderId={101}
        />
        {previewOnly ? null : (
          <HeadContent
            contents={{
              content: sharingImage?.src,
              width: sharingImage?.w,
              height: sharingImage?.h,
              structuredData: sectionData,
              url: route.content(id, title),
            }}
          />
        )}
        <GeneralHeader
          title={toRenderTitle}
          titleRaw={title_raw}
          subtitleLang={subtitle_lang}
          subtitle={toRenderSubtitle}
          subtitleRaw={subtitle_raw}
        />
        <RecipeImage
          instagramThumbnailUrl={instagram_thumbnail_url}
          instagramPostUrl={instagram_post_url}
          instagramAuthor={instagram_author_name}
          instagramThumbnailWidth={instagram_thumbnail_width}
          instagramThumbnailHeight={instagram_thumbnail_height}
          instagramHtml={instagram_html}
          instagramWidth={instagram_width}
          graphics={graphics}
          enableLightbox={!is_preview}
          image={primary_image}
        />
        <div className="recipe__columns">
          <section className="recipe__toolbox">
            <GeneralActions
              canPrint={canPrint}
              contentId={id}
              contentType={CONTENT_TYPES.SECTION}
              hash={hash}
              title={toRenderTitle}
              text={textToShare}
            />
          </section>
        </div>
        <div className="recipe__columns">
          <section className="recipe__column margin-bottom-40" />
          <section className="recipe__column margin-bottom-40">
            <AppearsIn book={book} id={id} loading={is_skeleton} />
          </section>
        </div>
      </React.Fragment>
    );
    const behindPaywall = (
      <ContentRaw
        id={id}
        contents={content_raw}
        image={{
          credit: book.image_credit,
          primary: primary_image?.src,
          permission: book.image_permission,
        }}
        isPreview={previewOnly || is_preview}
        instagramHtml={instagram_html}
        instagramWidth={instagram_width}
        book={book}
        graphics={graphics}
        loading={is_skeleton}
      />
    );
    const contentExtra = (
      <ContentExtra
        book={book}
        forceSsr={forceSsr}
        hash={hash}
        id={id}
        isPreview={is_preview}
        nestedContent={nested_content}
        next={next}
        parents={parents}
        prev={prev}
        title={title}
        type="Section"
        videos={videos}
      />
    );
    const afterPaywall =
      previewOnly || is_skeleton ? null : (
        <NoSsr defer fallback={forceSsr ? contentExtra : null}>
          {contentExtra}
        </NoSsr>
      );
    const contiguousContent = (
      <React.Fragment>
        {abovePaywall}
        <InlinePaywall
          alacarte={alacarte}
          sticky
          visible={is_preview}
          loading={loading || checkingAccess}
          error={paywallError}
          refreshAccessPolicy={refreshAccessPolicy}
          isbn={book.isbn}
          usingAdminAccess={usingAdminAccess}
        >
          {behindPaywall}
        </InlinePaywall>
        {afterPaywall}
      </React.Fragment>
    );
    const rootClasses = classNames('recipe', {
      'recipe--preview': is_preview,
      'recipe--no-copy': !canCopy,
      'recipe--no-print': !canPrint,
    });
    if (!enableSidebarAd) {
      return (
        <div className={rootClasses} data-cy={previewOnly ? 'content-preview' : 'section'}>
          {contiguousContent}
        </div>
      );
    }
    if (is_preview) {
      // Double track for advert - with paywall between runs
      return (
        <div className={rootClasses} data-cy={previewOnly ? 'content-preview' : 'recipe'}>
          <div className="recipe__outer-cols">
            <div className="recipe__content-col">{abovePaywall}</div>
            <div className="recipe__ad-col recipe__ad-col--extend-over-fog">
              <Advert
                slotName="floating-sidebar"
                format="vertical"
                hideOnMobile
                fullWidthResponsive={false}
                ezoicPlaceholderId={109}
                className="recipe__sidebar-ad1"
                lazy
              />
            </div>
          </div>
          <InlinePaywall
            alacarte={alacarte}
            sticky
            visible={is_preview}
            loading={loading || checkingAccess}
            error={paywallError}
            refreshAccessPolicy={refreshAccessPolicy}
            isbn={book.isbn}
            usingAdminAccess={usingAdminAccess}
          >
            <div className="recipe__outer-cols">
              <div className="recipe__content-col">{behindPaywall}</div>
              <div className="recipe__ad-col">
                <div className="recipe__sidebar-ad1" />
              </div>
            </div>
          </InlinePaywall>
          <div className="recipe__outer-cols">
            <div className="recipe__content-col">{afterPaywall}</div>
            <div className="recipe__ad-col">
              <Advert
                slotName="floating-sidebar"
                format="vertical"
                hideOnMobile
                fullWidthResponsive={false}
                ezoicPlaceholderId={110}
                className="recipe__sidebar-ad2"
                lazy
              />
            </div>
          </div>
        </div>
      );
    }
    // Single track for advert
    return (
      <div className={rootClasses} data-cy={previewOnly ? 'content-preview' : 'recipe'}>
        <div className="recipe__outer-cols">
          <div className="recipe__content-col">{contiguousContent}</div>
          <div className="recipe__ad-col">
            <Advert
              slotName="floating-sidebar"
              format="vertical"
              hideOnMobile
              fullWidthResponsive={false}
              ezoicPlaceholderId={109}
              className="recipe__sidebar-ad-single"
              lazy
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = {};

const mapStateToProps = state => ({
  userId: state.user.id,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(CssRecipe)(withUserAccessLogger(CONTENT_TYPES.SECTION, SectionComponent)));
